// src/hooks/useBilling.js - FIXED VERSION with proper usage stats integration

import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api, { billingAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useBilling = () => {
    const { user, refreshUser, updateUser } = useAuth();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Local state for UI management
    const [sessionDetails, setSessionDetails] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showCanceled, setShowCanceled] = useState(false);
    const [showUpdated, setShowUpdated] = useState(false);
    const [showReactivated, setShowReactivated] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [purchasesEnabled, setPurchasesEnabled] = useState(true);


    useEffect(() => {
        const fetchData = async () => {
            // ... existing fetch logic
            const pricingResponse = await api.get('/billing/pricing');
            if (pricingResponse.data.success) {
                setPurchasesEnabled(pricingResponse.data.data.purchasesEnabled ?? true);
            }
        };
        fetchData();
    }, []);

    // ADDED: Usage stats query
    const {
        data: usageStatsResponse,
        isLoading: usageStatsLoading,
        refetch: refetchUsageStats
    } = useQuery(
        'usageStats',
        () => fetch('/api/settings/usage-stats', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(res => res.json()),
        {
            retry: 2,
            staleTime: 30000, // 30 seconds
            refetchInterval: 60000, // Refetch every minute
            onError: (error) => {
                console.error('Usage stats fetch error:', error);
            }
        }
    );

    // ADDED: Quota info query
    const {
        data: quotaResponse,
        isLoading: quotaLoading,
        refetch: refetchQuota
    } = useQuery(
        'quotaInfo',
        () => fetch('/api/settings/quota', {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            }
        }).then(res => res.json()),
        {
            retry: 2,
            staleTime: 30000,
            refetchInterval: 60000,
            onError: (error) => {
                console.error('Quota fetch error:', error);
            }
        }
    );

    // Subscription data query with proper error handling
    const {
        data: subscriptionResponse,
        isLoading: subscriptionLoading,
        refetch: refetchSubscription,
        error: subscriptionError
    } = useQuery(
        'subscription',
        billingAPI.getSubscription,
        {
            retry: 2,
            refetchInterval: false,
            refetchIntervalInBackground: false,
            onError: (error) => {
                if (error.response?.status !== 404 && error.response?.status !== 501) {
                    console.error('Subscription API error:', error);
                }
            }
        }
    );

    // Pricing data query
    const {
        data: pricingResponse,
        isLoading: pricingLoading,
        error: pricingError
    } = useQuery(
        'pricing',
        billingAPI.getPricing,
        {
            retry: 2,
            staleTime: 10 * 60 * 1000,
            onError: (error) => {
                console.error('Pricing API error:', error);
            }
        }
    );

    // Extract data from API responses correctly
    const subscription = subscriptionResponse?.data?.data;
    const plans = pricingResponse?.data?.data?.plans || [];
    const stripeConfigured = pricingResponse?.data?.data?.stripeConfigured || false;

    // ADDED: Extract usage stats and quota info
    const usageStats = usageStatsResponse?.data?.usage || {
        generated: 0,
        published: 0,
        remaining: 10,
        limit: 10,
        usagePercent: 0
    };

    const quotaInfo = quotaResponse?.data || {
        plan: user?.plan || 'free',
        limits: { postsPerMonth: 10 },
        usage: { postsGenerated: 0, remaining: 10 }
    };

    // console.log('🔍 Billing Debug with Usage:', {
    //     subscriptionResponse: subscriptionResponse?.data,
    //     subscription,
    //     user: user,
    //     userPlan: user?.plan,
    //     userEntitlement: user?.entitlement,
    //     usageStats,
    //     quotaInfo,
    //     usageStatsLoading,
    //     quotaLoading
    // });

    // URL parameter handling for post-payment redirects
    useEffect(() => {
        const success = searchParams.get('success');
        const canceled = searchParams.get('canceled');
        const updated = searchParams.get('updated');
        const reactivated = searchParams.get('reactivated');
        const sessionId = searchParams.get('session_id');
        const newPlan = searchParams.get('plan');

        if (success === 'true' && sessionId) {
            handlePaymentSuccess(sessionId);
        } else if (canceled === 'true') {
            handlePaymentCanceled();
        } else if (updated === 'true') {
            handleSubscriptionUpdated(newPlan);
        } else if (reactivated === 'true') {
            handleSubscriptionReactivated();
        }
    }, [searchParams]);

    // Payment success handler
    const handlePaymentSuccess = useCallback(async (sessionId) => {
        setShowSuccess(true);
        setIsProcessing(true);
        toast.success('Payment successful! Your subscription is being activated.');

        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            if (!token) {
                console.error('❌ No authentication token found for session details');
                toast.error('Authentication required. Please log in again.');
                window.location.href = '/login';
                return;
            }

            try {
                const response = await fetch(`/api/billing/session/${sessionId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    credentials: 'include'
                });

                if (response.ok) {
                    const details = await response.json();
                    // console.log('✅ Session details retrieved:', details);
                    setSessionDetails(details.data);

                    // Update user plan if available
                    if (details.data?.plan && details.data.plan !== user?.plan) {
                        updateUser({
                            plan: details.data.plan,
                            entitlement: details.data.plan
                        });
                    }
                } else if (response.status === 401) {
                    console.error('❌ 401 Unauthorized - token may be invalid');
                    toast.error('Session expired. Please log in again.');
                    setTimeout(() => window.location.href = '/login', 3000);
                    return;
                } else {
                    console.warn('⚠️ Could not fetch session details, but payment was successful');
                    toast.warn('Could not fetch transaction details, but payment was successful');
                }
            } catch (sessionError) {
                console.error('❌ Session details request error:', sessionError);
                toast.warn('Could not fetch transaction details, but payment was successful');
            }

            // UPDATED: Always refresh all data including usage stats
            await Promise.all([
                refreshUser(),
                refetchSubscription(),
                refetchUsageStats(),
                refetchQuota(),
                queryClient.invalidateQueries('subscription'),
                queryClient.invalidateQueries('usageStats'),
                queryClient.invalidateQueries('quotaInfo')
            ]);

        } catch (error) {
            console.error('❌ Failed to process payment success:', error);
        }

        // Auto-cleanup after 10 seconds
        setTimeout(() => cleanupUrlAndState('success'), 10000);
    }, [refetchSubscription, refetchUsageStats, refetchQuota, queryClient, updateUser, user?.plan, refreshUser]);

    // Payment canceled handler
    const handlePaymentCanceled = useCallback(() => {
        setShowCanceled(true);
        toast.error('Payment was canceled. You can try again when ready.');
        setTimeout(() => cleanupUrlAndState('canceled'), 5000);
    }, []);

    // Subscription updated handler
    const handleSubscriptionUpdated = useCallback(async (newPlan) => {
        setShowUpdated(true);
        setIsProcessing(true);
        toast.success(`Successfully updated to ${newPlan} plan!`);

        // UPDATED: Refresh usage stats after plan change
        await Promise.all([
            refreshUser(),
            refetchSubscription(),
            refetchUsageStats(),
            refetchQuota(),
            queryClient.invalidateQueries('subscription'),
            queryClient.invalidateQueries('usageStats'),
            queryClient.invalidateQueries('quotaInfo')
        ]);

        setTimeout(() => cleanupUrlAndState('updated'), 8000);
    }, [refetchSubscription, refetchUsageStats, refetchQuota, queryClient, refreshUser]);

    // Subscription reactivated handler
    const handleSubscriptionReactivated = useCallback(async () => {
        setShowReactivated(true);
        setIsProcessing(true);
        toast.success('Subscription reactivated successfully!');

        await Promise.all([
            refreshUser(),
            refetchSubscription(),
            refetchUsageStats(),
            refetchQuota(),
            queryClient.invalidateQueries('subscription'),
            queryClient.invalidateQueries('usageStats'),
            queryClient.invalidateQueries('quotaInfo')
        ]);

        setTimeout(() => cleanupUrlAndState('reactivated'), 8000);
    }, [refetchSubscription, refetchUsageStats, refetchQuota, queryClient, refreshUser]);

    // URL and state cleanup
    const cleanupUrlAndState = useCallback((type) => {
        navigate('/billing', { replace: true });

        if (type === 'success') setShowSuccess(false);
        if (type === 'canceled') setShowCanceled(false);
        if (type === 'updated') setShowUpdated(false);
        if (type === 'reactivated') setShowReactivated(false);

        setIsProcessing(false);
    }, [navigate]);

    // Helper function to get auth token
    const getAuthToken = () => {
        return localStorage.getItem('token') || sessionStorage.getItem('token');
    };

    // Checkout/Upgrade mutation
    const checkoutMutation = useMutation(billingAPI.createCheckout, {
        onMutate: () => {
            // console.log('🔄 Starting checkout...');
            setIsProcessing(true);
        },
        onSuccess: (response) => {
            // console.log('✅ Checkout response received:', response);
            const apiResponse = response.data;

            if (apiResponse?.success && apiResponse?.data?.url) {
                // console.log('🔗 Redirecting to Stripe:', apiResponse.data.url);
                window.location.href = apiResponse.data.url;
            } else {
                console.error('❌ Invalid response structure:', response);
                toast.error('Invalid response from server');
                setIsProcessing(false);
            }
        },
        onError: (error) => {
            setIsProcessing(false);
            console.error('❌ Checkout error:', error);
            toast.error(error.response?.data?.message || 'Failed to create checkout session');
        },
    });

    // FIXED: Downgrade mutation with usage stats refresh
    const downgradeMutation = useMutation(
        async (targetPlan) => {
            const token = getAuthToken();
            if (!token) throw new Error('No authentication token');

            const response = await fetch('/api/billing/downgrade', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ targetPlan })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            return response.json();
        },
        {
            onMutate: () => setIsProcessing(true),
            onSuccess: (data) => {
                toast.success(data.message || 'Subscription downgraded successfully');
                // UPDATED: Refresh all data including usage stats
                Promise.all([
                    refreshUser(),
                    refetchSubscription(),
                    refetchUsageStats(),
                    refetchQuota(),
                    queryClient.invalidateQueries('subscription'),
                    queryClient.invalidateQueries('usageStats'),
                    queryClient.invalidateQueries('quotaInfo')
                ]).finally(() => setIsProcessing(false));
            },
            onError: (error) => {
                setIsProcessing(false);
                toast.error(error.message || 'Failed to downgrade subscription');
                console.error('Downgrade error:', error);
            },
        }
    );

    // FIXED: Cancel mutation with usage stats refresh
    const cancelMutation = useMutation(
        async ({ immediate, reason }) => {
            const token = getAuthToken();
            if (!token) throw new Error('No authentication token');

            const response = await fetch('/api/billing/cancel', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    immediate,
                    reason,
                    confirmNoRefunds: true
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            return response.json();
        },
        {
            onMutate: () => setIsProcessing(true),
            onSuccess: (data) => {
                toast.success(data.message || 'Subscription canceled successfully');
                Promise.all([
                    refreshUser(),
                    refetchSubscription(),
                    refetchUsageStats(),
                    refetchQuota(),
                    queryClient.invalidateQueries('subscription'),
                    queryClient.invalidateQueries('usageStats'),
                    queryClient.invalidateQueries('quotaInfo')
                ]).finally(() => setIsProcessing(false));
            },
            onError: (error) => {
                setIsProcessing(false);
                toast.error(error.message || 'Failed to cancel subscription');
                console.error('Cancel error:', error);
            },
        }
    );

    // FIXED: Reactivate mutation with usage stats refresh
    const reactivateMutation = useMutation(
        async () => {
            const token = getAuthToken();
            if (!token) throw new Error('No authentication token');

            const response = await fetch('/api/billing/reactivate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || `HTTP ${response.status}`);
            }

            return response.json();
        },
        {
            onMutate: () => setIsProcessing(true),
            onSuccess: (data) => {
                toast.success(data.message || 'Subscription reactivated successfully');
                Promise.all([
                    refreshUser(),
                    refetchSubscription(),
                    refetchUsageStats(),
                    refetchQuota(),
                    queryClient.invalidateQueries('subscription'),
                    queryClient.invalidateQueries('usageStats'),
                    queryClient.invalidateQueries('quotaInfo')
                ]).finally(() => setIsProcessing(false));
            },
            onError: (error) => {
                setIsProcessing(false);
                toast.error(error.message || 'Failed to reactivate subscription');
                console.error('Reactivate error:', error);
            },
        }
    );

    // UPDATED: Manual refresh function with usage stats
    const manualRefresh = useCallback(async () => {
        toast.loading('Refreshing subscription data...', { id: 'refresh' });

        try {
            await Promise.all([
                refreshUser(),
                refetchSubscription(),
                refetchUsageStats(),
                refetchQuota(),
                queryClient.invalidateQueries('subscription'),
                queryClient.invalidateQueries('usageStats'),
                queryClient.invalidateQueries('quotaInfo')
            ]);

            toast.success('Subscription data refreshed!', { id: 'refresh' });
        } catch (error) {
            toast.error('Failed to refresh data', { id: 'refresh' });
            console.error('Refresh error:', error);
        }
    }, [refreshUser, refetchSubscription, refetchUsageStats, refetchQuota, queryClient]);

    // Computed values with proper data flow
    const currentPlan = user?.plan || 'free';
    const isLoading = subscriptionLoading || pricingLoading || usageStatsLoading || quotaLoading;

    // Plan comparison helpers
    const canUpgradeTo = useCallback((planName) => {
        const planLevels = { free: 0, pro: 1, enterprise: 2 };
        const currentLevel = planLevels[currentPlan] || 0;
        const targetLevel = planLevels[planName.toLowerCase()] || 0;
        return targetLevel > currentLevel && stripeConfigured;
    }, [currentPlan, stripeConfigured]);

    const canDowngradeTo = useCallback((planName) => {
        const planLevels = { free: 0, pro: 1, enterprise: 2 };
        const currentLevel = planLevels[currentPlan] || 0;
        const targetLevel = planLevels[planName.toLowerCase()] || 0;
        return targetLevel < currentLevel;
    }, [currentPlan]);

    // Action handlers
    const handleUpgrade = useCallback((priceId) => {
        if (!priceId) {
            toast.error('This plan is not available yet');
            return;
        }
        if (!purchasesEnabled) {
            alert('License purchases are temporarily disabled. Please try again later.');
            return;
        }
        checkoutMutation.mutate(priceId);
    }, [checkoutMutation]);

    const handleDowngrade = useCallback((targetPlan) => {
        downgradeMutation.mutate(targetPlan);
    }, [downgradeMutation]);

    const handleCancel = useCallback((options = {}) => {
        cancelMutation.mutate(options);
    }, [cancelMutation]);

    const handleReactivate = useCallback(() => {
        reactivateMutation.mutate();
    }, [reactivateMutation]);

    return {
        // Data - properly structured
        subscription,
        plans,
        currentPlan,
        user,
        sessionDetails,

        // ADDED: Usage and quota data
        usageStats,
        quotaInfo,

        // Loading states
        isLoading,
        isProcessing,
        subscriptionLoading,
        pricingLoading,
        usageStatsLoading,
        quotaLoading,

        // Configuration
        stripeConfigured,
        purchasesEnabled,

        // UI states
        showSuccess,
        showCanceled,
        showUpdated,
        showReactivated,

        // Actions
        handleUpgrade,
        handleDowngrade,
        handleCancel,
        handleReactivate,
        manualRefresh,

        // ADDED: Usage stats refresh
        refetchUsageStats,
        refetchQuota,

        // Mutation states
        checkoutMutation,
        downgradeMutation,
        cancelMutation,
        reactivateMutation,

        // Helpers
        canUpgradeTo,
        canDowngradeTo,

        // State setters for manual control
        setShowSuccess,
        setShowCanceled,
        setShowUpdated,
        setShowReactivated,

        // Errors
        subscriptionError,
        pricingError
    };
};

// FIXED: Subscription status helper hook
export const useSubscriptionStatus = () => {
    const { subscription, currentPlan, user } = useBilling();

    // console.log('🔍 Subscription Status Debug:', {
    //     subscription,
    //     currentPlan,
    //     user: {
    //         plan: user?.plan,
    //         entitlement: user?.entitlement,
    //         subscriptionStatus: user?.subscriptionStatus
    //     }
    // });

    const isActive = subscription?.status === 'active';
    const isPending = subscription?.status === 'trialing' || subscription?.status === 'past_due';
    const isCanceled = subscription?.status === 'canceled' || subscription?.status === 'unpaid';
    const willCancel = subscription?.cancelAtPeriodEnd === true;
    const hasSubscription = Boolean(subscription?.subscriptionId);

    const statusText = React.useMemo(() => {
        if (isCanceled) return 'Canceled';
        if (willCancel) return 'Canceling at period end';
        if (isPending) return 'Pending';
        if (isActive) return 'Active';
        if (hasSubscription) return 'Subscription found';
        return 'No subscription';
    }, [isActive, isPending, isCanceled, willCancel, hasSubscription]);

    const statusColor = React.useMemo(() => {
        if (isCanceled) return 'text-red-600';
        if (willCancel) return 'text-orange-600';
        if (isPending) return 'text-yellow-600';
        if (isActive) return 'text-green-600';
        return 'text-gray-600';
    }, [isActive, isPending, isCanceled, willCancel]);

    return {
        isActive,
        isPending,
        isCanceled,
        willCancel,
        hasSubscription,
        statusText,
        statusColor,
        currentPlan,
        renewalDate: subscription?.currentPeriodEnd
            ? new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()
            : null
    };
};